/**
 * Responsibility: turn one airfield's layout into line segments.
 *
 * ## The runway is level and the ground is not, which is the whole design
 *
 * A runway has to be dead flat over roughly a thousand units, and no terrain in this world is. The obvious answer —
 * flatten the ground under it — is the wrong one twice over. It inverts the rule that structures sample terrain and never
 * the reverse, and it would not even work: blended elevation is a weighted sum, so a pad at 70% biome weight comes out 70%
 * flat plus 30% of whatever the neighbour is doing, which tilts the runway anyway.
 *
 * So the runway sits at **one constant height** and the drop to the real ground is drawn as an **embankment**: a post down
 * to the ground at intervals along each edge, with their feet joined up. That is what a runway crossing uneven ground
 * actually has, it needs no change to the terrain, and it turns the flatness problem into a cosmetic budget — the only
 * question left is how tall an embankment is tolerable, which is one number in `config.js`.
 *
 * ## Segments in the shape the buildings already use
 *
 * Each function here returns `{x1,y1,z1,x2,y2,z2,t1,t2}` records, exactly what `emitShapeSegments` consumes for every
 * building and bridge in the world. **So airfields needed no new emitter.**
 *
 * ## Why `t` is local to each element rather than global
 *
 * Everywhere else `t` is a height fraction over the whole structure. Here that would be wrong: the tower is over a hundred
 * units tall and the runway is flat on the ground, so a global fraction would draw the runway — the brightest, most
 * legible thing on the site — in the darkest end of the gradient. Instead each element spans the gradient on its own
 * terms: embankments run dark at the ground to bright at the runway edge, the tower runs dark at its base to bright at the
 * cab, and the runway surface is simply bright. The gradient is being used to pick out structure, not to measure height.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { AIRFIELD } from '../config.js';

/** A segment record in the shape `emitShapeSegments` consumes. */
function line([x1, y1, z1], [x2, y2, z2], t1, t2) {
  return { x1, y1, z1, x2, y2, z2, t1, t2: t2 ?? t1 };
}

/**
 * The frame a runway is built in.
 *
 * @param {{x: number, z: number, heading: number}} runway
 * @returns {(along: number, across: number, y: number) => [number, number, number]} A point from a distance along the
 *   centreline, an offset across it, and a height. Both offsets are in world units and measured from the centre.
 */
function frameOf({ x, z, heading }) {
  const alongX = Math.cos(heading);
  const alongZ = Math.sin(heading);

  return (along, across, y) => [
    x + alongX * along - alongZ * across,
    y,
    z + alongZ * along + alongX * across,
  ];
}

/**
 * The runway surface: two edges, threshold bars at both ends, and a dashed centreline.
 *
 * All at one height, because that is what a runway is. The centreline is the single most legible thing about it from the
 * air — two parallel edges alone read as a road.
 */
function surfaceSegments(runway, deckY) {
  const at = frameOf(runway);
  const half = runway.length / 2;
  const side = AIRFIELD.RUNWAY_WIDTH / 2;
  const segments = [];

  // Edges. One segment each: the surface is level, so there is nothing to subdivide for.
  for (const across of [-side, side]) {
    segments.push(line(at(-half, across, deckY), at(half, across, deckY), 1));
  }

  /*
    Threshold bars. The bar at zero depth closes the end of the runway, so the surface reads as a closed rectangle
    without needing a separate end line.
  */
  for (const end of [-1, 1]) {
    for (let i = 0; i < AIRFIELD.THRESHOLD_BARS; i += 1) {
      const inset = AIRFIELD.THRESHOLD_BARS === 1 ? 0 : (i / (AIRFIELD.THRESHOLD_BARS - 1)) * AIRFIELD.THRESHOLD_DEPTH;
      const along = end * (half - inset);
      segments.push(line(at(along, -side, deckY), at(along, side, deckY), 1));
    }
  }

  /*
    Centreline dashes, laid between the two threshold blocks so they do not collide with the bars. Each dash is centred in
    its own slot and covers a fraction of it, which is what keeps it a dashed line rather than a solid stripe.
  */
  const marked = half - AIRFIELD.THRESHOLD_DEPTH;

  if (marked > 0) {
    const slot = (2 * marked) / AIRFIELD.CENTRELINE_DASHES;
    const dash = (slot * AIRFIELD.CENTRELINE_DASH_RATIO) / 2;

    for (let i = 0; i < AIRFIELD.CENTRELINE_DASHES; i += 1) {
      const center = -marked + slot * (i + 0.5);
      segments.push(line(at(center - dash, 0, deckY), at(center + dash, 0, deckY), 1));
    }
  }

  return segments;
}

/**
 * The embankment: a post from each edge down to the ground, with the feet joined up.
 *
 * The joined feet are what matter. Posts alone hang off the runway like whiskers; connecting them draws the bottom edge of
 * a bank of earth, and the eye fills in the face between the two lines.
 *
 * @param {object} runway
 * @param {number} deckY - Height of the runway surface.
 * @param {(x: number, z: number) => number} sampleGround
 */
function embankmentSegments(runway, deckY, sampleGround) {
  const at = frameOf(runway);
  const half = runway.length / 2;
  const side = AIRFIELD.RUNWAY_WIDTH / 2;
  const steps = AIRFIELD.SKIRT_STEPS;
  const segments = [];

  for (const across of [-side, side]) {
    /** Foot of each post, or null where the ground is already at the surface and there is nothing to draw. */
    const feet = [];

    for (let i = 0; i < steps; i += 1) {
      const along = -half + (runway.length * i) / (steps - 1);
      const [x, , z] = at(along, across, deckY);

      /*
        Clamped to the surface. Placement picks `deckY` as the highest ground under this exact set of points, so the
        ground here can never be above it — the clamp is a guard against that invariant being broken elsewhere rather
        than a live case, and it fails safe by drawing nothing instead of an upside-down post.
      */
      const groundY = Math.min(sampleGround(x, z), deckY);
      const point = at(along, across, groundY);

      feet.push(point);

      // Nothing worth drawing where the runway meets the ground it was levelled to.
      if (deckY - groundY > 0) segments.push(line(point, at(along, across, deckY), 0, 1));
    }

    for (let i = 0; i < feet.length - 1; i += 1) {
      segments.push(line(feet[i], feet[i + 1], 0));
    }
  }

  return segments;
}

/**
 * The control tower: a slender shaft with a wider cab overhanging its top.
 *
 * **The overhang is the entire silhouette.** A plain box on a stalk is a water tower; a box that is wider than what
 * carries it is a control tower and nothing else, which matters in a world where every other tall thin thing is either a
 * building or a pylon.
 *
 * @param {{x: number, z: number, heading: number, groundY: number}} tower
 * @param {number} deckY - The tower's height is measured from the runway surface, so it stands level with the field it
 *   serves rather than however high its own patch of ground happens to be.
 */
function towerSegments(tower, deckY) {
  const at = frameOf(tower);
  const shaft = AIRFIELD.TOWER_WIDTH / 2;
  const cab = shaft + AIRFIELD.TOWER_CAB_OVERHANG;

  const shaftTop = deckY + AIRFIELD.TOWER_HEIGHT;
  const cabTop = shaftTop + AIRFIELD.TOWER_CAB_HEIGHT;
  const span = Math.max(1, cabTop - tower.groundY);

  /** Height fraction over the tower's own extent, so the shaft darkens into the ground and the cab is brightest. */
  const fraction = (y) => Math.min(1, Math.max(0, (y - tower.groundY) / span));

  /** The four corners of a square of a given half-width, in order, closing back on the first. */
  const corners = (halfWidth) => [
    [-halfWidth, -halfWidth],
    [halfWidth, -halfWidth],
    [halfWidth, halfWidth],
    [-halfWidth, halfWidth],
  ];

  /** A closed loop of four segments at one height. */
  const ring = (halfWidth, y) => {
    const points = corners(halfWidth);
    return points.map((point, i) =>
      line(at(point[0], point[1], y), at(points[(i + 1) % 4][0], points[(i + 1) % 4][1], y), fraction(y)),
    );
  };

  const segments = [];

  // Shaft: a post at each corner from the ground up.
  for (const [along, across] of corners(shaft)) {
    segments.push(line(at(along, across, tower.groundY), at(along, across, shaftTop), fraction(tower.groundY), fraction(shaftTop)));
  }

  // Bands up the shaft, which is what gives it scale against an empty sky.
  for (let i = 1; i <= AIRFIELD.TOWER_RINGS; i += 1) {
    const y = tower.groundY + ((shaftTop - tower.groundY) * i) / (AIRFIELD.TOWER_RINGS + 1);
    segments.push(...ring(shaft, y));
  }

  // Cab: a floor, a roof, corner posts, and a strut out to each overhanging corner.
  segments.push(...ring(cab, shaftTop));
  segments.push(...ring(cab, cabTop));

  const cabCorners = corners(cab);
  const shaftCorners = corners(shaft);

  for (let i = 0; i < 4; i += 1) {
    segments.push(
      line(at(cabCorners[i][0], cabCorners[i][1], shaftTop), at(cabCorners[i][0], cabCorners[i][1], cabTop), fraction(shaftTop), 1),
    );
    segments.push(
      line(at(shaftCorners[i][0], shaftCorners[i][1], shaftTop), at(cabCorners[i][0], cabCorners[i][1], shaftTop), fraction(shaftTop)),
    );
  }

  return segments;
}

/**
 * A parked aircraft: fuselage, wings, tailplane and fin.
 *
 * ## Defined here rather than imported from the flight layer
 *
 * `flight/aircraft/default.js` holds a vertex list that would serve, and reusing it would be tempting DRY. It is the wrong
 * call: the world would then depend on the flight layer, and the player's own aircraft could never be restyled without
 * silently restyling the scenery. Five segments is not worth that coupling.
 *
 * @param {{x: number, z: number, heading: number}} aircraft
 * @param {number} deckY - Runway surface it is standing on.
 */
function aircraftSegments(aircraft, deckY) {
  const at = frameOf(aircraft);
  const half = AIRFIELD.AIRCRAFT_LENGTH / 2;
  const wing = AIRFIELD.AIRCRAFT_SPAN / 2;
  const tail = wing * AIRFIELD.AIRCRAFT_TAIL_SPAN_RATIO;

  // Standing on its undercarriage rather than lying flat on the surface.
  const body = deckY + AIRFIELD.AIRCRAFT_RIDE_HEIGHT;

  // Wings sit forward of the middle, which is what stops it reading as a plain cross.
  const station = half - AIRFIELD.AIRCRAFT_LENGTH * AIRFIELD.AIRCRAFT_WING_STATION;

  return [
    line(at(half, 0, body), at(-half, 0, body), 1),
    line(at(station, -wing, body), at(station, wing, body), 1),
    line(at(-half, -tail, body), at(-half, tail, body), 1),
    line(at(-half, 0, body), at(-half, 0, body + AIRFIELD.AIRCRAFT_FIN_HEIGHT), 1),
  ];
}

/**
 * Every line segment one airfield is made of.
 *
 * @param {object} airfield
 * @param {number} airfield.deckY - The one height every runway surface sits at. A field is one level plane, so a single
 *   value serves all of its runways.
 * @param {Array<object>} airfield.runways - Each `{x, z, heading, length}`.
 * @param {object} airfield.tower - `{x, z, heading, groundY}`.
 * @param {object|null} airfield.aircraft - `{x, z, heading}`, or null.
 * @param {(x: number, z: number) => number} sampleGround - Ground elevation, for the embankments. Supplied by the caller
 *   rather than imported, so this module needs no terrain dependency and stays trivially testable.
 * @returns {Array<object>} Segment records for `emitShapeSegments`.
 */
export function buildAirfield(airfield, sampleGround) {
  const segments = [];

  for (const runway of airfield.runways) {
    segments.push(...surfaceSegments(runway, airfield.deckY));
    segments.push(...embankmentSegments(runway, airfield.deckY, sampleGround));
  }

  segments.push(...towerSegments(airfield.tower, airfield.deckY));

  if (airfield.aircraft) segments.push(...aircraftSegments(airfield.aircraft, airfield.deckY));

  return segments;
}

/** Exported for tests and budgets, which is also the only reason these are separable. */
export { surfaceSegments, embankmentSegments, towerSegments, aircraftSegments };
